import { useState, useMemo } from 'react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { CustomPagination } from '@/components/CustomPagination'
import {
  Search,
  AlertCircle,
  Star,
  FileText,
  Calendar,
  Zap,
  TrendingUp,
  Sparkles,
  ShieldCheck,
  ShieldX,
  ChevronLeft,
  ChevronRight,
  Users,
  Flag,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MessageSquare,
  CheckCircle2,
  AlertTriangle,
  Quote,
  FileSearch,
  Eye,
} from 'lucide-react'
import {
  useGetDescriptionQuery,
  useGetWeeklySummaryQuery,
  type DescriptionAnalysisItem,
  type WeeklySummaryItem,
  type WeeklyDescriptionEntry,
} from '@/redux/service/bot'
import { useDebounce } from '@/lib/useDebounce'
import { format, startOfWeek, endOfWeek, addWeeks, subWeeks } from 'date-fns'
import { cn, formatHours } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

const RatingStars = ({ rating }: { rating: number }) => {
  return (
    <div className='flex gap-0.5'>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(star => (
        <Star
          key={star}
          className={cn(
            'h-3 w-3',
            star <= rating ? 'fill-amber-400 text-amber-400' : 'text-[#262626]'
          )}
        />
      ))}
    </div>
  )
}

const DescriptionAnalysis = () => {
  const [activeTab, setActiveTab] = useState<1 | 2>(1)
  const [selectedUser, setSelectedUser] = useState<WeeklySummaryItem | null>(
    null
  )
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)

  // Weekly summary state
  const [weekOffset, setWeekOffset] = useState(-1)
  const [summaryPage, setSummaryPage] = useState(1)
  const [summaryLimit, setSummaryLimit] = useState(100)

  const debouncedSearch = useDebounce(searchQuery, 500)

  const [summarySortBy, setSummarySortBy] = useState<string | undefined>()
  const [summaryOrder, setSummaryOrder] = useState<'ASC' | 'DESC' | undefined>()

  const handleSummarySort = (column: string) => {
    if (summarySortBy === column) {
      if (summaryOrder === 'ASC') {
        setSummaryOrder('DESC')
      } else if (summaryOrder === 'DESC') {
        setSummarySortBy(undefined)
        setSummaryOrder(undefined)
      }
    } else {
      setSummarySortBy(column)
      setSummaryOrder('ASC')
    }
    setSummaryPage(1)
  }

  const getSummarySortIcon = (column: string) => {
    if (summarySortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-1 opacity-50 inline-block' />
    if (summaryOrder === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-1 inline-block' />
    return <ArrowDown className='w-3 h-3 ml-1 inline-block' />
  }

  const [descSortBy, setDescSortBy] = useState<string | undefined>()
  const [descOrder, setDescOrder] = useState<'ASC' | 'DESC' | undefined>()

  const handleDescSort = (column: string) => {
    if (descSortBy === column) {
      if (descOrder === 'ASC') {
        setDescOrder('DESC')
      } else if (descOrder === 'DESC') {
        setDescSortBy(undefined)
        setDescOrder(undefined)
      }
    } else {
      setDescSortBy(column)
      setDescOrder('ASC')
    }
    setPage(1)
  }

  const getDescSortIcon = (column: string) => {
    if (descSortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-1 opacity-50 inline-block' />
    if (descOrder === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-1 inline-block' />
    return <ArrowDown className='w-3 h-3 ml-1 inline-block' />
  }

  // Calculate week range based on offset (Monday → Sunday)
  const { weekStart, weekEnd, weekLabel } = useMemo(() => {
    const now = new Date()
    const targetDate =
      weekOffset === 0
        ? now
        : weekOffset > 0
          ? addWeeks(now, weekOffset)
          : subWeeks(now, Math.abs(weekOffset))
    const ws = startOfWeek(targetDate, { weekStartsOn: 1 })
    const we = endOfWeek(targetDate, { weekStartsOn: 1 })
    return {
      weekStart: format(ws, 'yyyy-MM-dd'),
      weekEnd: format(we, 'yyyy-MM-dd'),
      weekLabel: `${format(ws, 'MMM dd')} – ${format(we, 'MMM dd, yyyy')}`,
    }
  }, [weekOffset])

  // Description analysis query
  const {
    data: apiResponse,
    isLoading,
    isFetching,
    error,
  } = useGetDescriptionQuery({
    page,
    limit,
    search: debouncedSearch,
    sort_by: descSortBy,
    order: descOrder,
  })

  // Weekly summary query
  const {
    data: summaryResponse,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    error: summaryError,
  } = useGetWeeklySummaryQuery({
    week_start: weekStart,
    week_end: weekEnd,
    page: summaryPage,
    limit: summaryLimit,
    search: debouncedSearch,
    sort_by: summarySortBy,
    order: summaryOrder,
  })

  const results = (apiResponse?.data || []).filter(
    item => item.rating >= 0 && item.rating <= 6
  )
  const totalItems = apiResponse?.total || 0
  const totalPages = Math.ceil(totalItems / limit)

  const summaryResults = summaryResponse?.data || []
  const summaryTotal = summaryResponse?.total || 0
  const summaryTotalPages = Math.ceil(summaryTotal / summaryLimit)

  // Stats from summary
  const passCount = summaryResults.filter(i => i.pass).length
  const failCount = summaryResults.filter(i => !i.pass).length

  return (
    <TooltipProvider>
      <div className='h-full flex flex-col p-2 space-y-6 bg-background text-foreground overflow-hidden'>
        {/* ─── Header ─── */}
        {/* <div className='shrink-0 relative overflow-hidden rounded-2xl bg-primary/5 border border-primary/10 px-5 py-4'>
          <div className='relative z-10 flex items-center justify-between gap-4'>
            <div className='flex items-center gap-3'>
              <div className='p-2 bg-primary text-primary-foreground rounded-xl shadow-sm shadow-primary/20'>
                <Zap className='h-5 w-5 fill-current' />
              </div>
              <div>
                <h1 className='text-2xl font-black tracking-tight text-foreground '>
                  Timesheet Intelligence
                </h1>
                <p className='text-muted-foreground text-xs mt-0.5'>
                  AI audit of descriptions, tags, and weekly performance.
                </p>
              </div>
            </div>
            <div className='hidden xl:flex flex-col items-end shrink-0'>
              <span className='text-[10px] font-bold uppercase tracking-widest text-muted-foreground/60'>
                System Status
              </span>
              <span className='text-xs font-bold text-emerald-500 flex items-center gap-1.5'>
                <span className='h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse' />
                AI Auditing Active
              </span>
            </div>
          </div>
          <div className='absolute -top-16 -right-16 h-32 w-32 bg-primary/5 rounded-full blur-3xl' />
        </div> */}
        {/* ─── Search & Filters Bar ─── */}
        <div className='shrink-0 flex flex-col md:flex-row md:items-center justify-between gap-4'>
          <div className='flex p-1.5 bg-muted/50 border border-border rounded-2xl gap-3 w-fit'>
            <button
              onClick={() => setActiveTab(1)}
              className={`px-5 py-2 text-xs font-bold transition-all duration-300 rounded-xl flex items-center gap-2 ${
                activeTab === 1
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-[1.02]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <TrendingUp className='h-3.5 w-3.5' />
              Weekly Scorecard
            </button>
            <button
              onClick={() => setActiveTab(2)}
              className={`px-5 py-2 text-xs font-bold transition-all duration-300 rounded-xl flex items-center gap-2 ${
                activeTab === 2
                  ? 'bg-primary text-primary-foreground shadow-lg shadow-primary/20 scale-[1.02]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              <MessageSquare className='h-3.5 w-3.5' />
              Description Analysis
            </button>
          </div>

          <div className='relative max-w-sm w-full group'>
            <div className='absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none'>
              <Search className='h-4 w-4 text-muted-foreground group-focus-within:text-primary transition-colors' />
            </div>
            <Input
              placeholder='Search employees...'
              value={searchQuery}
              onChange={e => {
                setSearchQuery(e.target.value)
                setPage(1)
                setSummaryPage(1)
              }}
              className='pl-11 h-11 border-border bg-card/50 hover:bg-card focus:bg-card text-foreground rounded-2xl transition-all shadow-sm focus:ring-4 focus:ring-primary/5'
            />
          </div>
        </div>
        {/* ─── Stats Cards ─── */}
        <div className='shrink-0 grid grid-cols-2 lg:grid-cols-4 gap-4'>
          {[
            {
              icon: FileText,
              label: 'Analyzed Entries',
              value: totalItems,
              color: 'primary',
              textColor: 'text-foreground',
            },
            {
              icon: Users,
              label: 'Active Employees',
              value: summaryTotal,
              color: 'indigo-500',
              textColor: 'text-foreground',
            },
            {
              icon: ShieldCheck,
              label: 'Passing Standard',
              value: passCount,
              color: 'emerald-500',
              textColor: 'text-emerald-500',
            },
            {
              icon: ShieldX,
              label: 'Audit Flags',
              value: failCount,
              color: 'red-500',
              textColor: 'text-red-500',
            },
          ].map(({ icon: Icon, label, value, color, textColor }) => (
            <div
              key={label}
              className='flex items-center gap-4 p-2 rounded-xl bg-card border border-border'
            >
              <div
                className={`h-9 w-9 rounded-xl bg-${color}/10 flex items-center justify-center shrink-0`}
              >
                <Icon className={`h-5 w-5 text-${color}`} />
              </div>
              <div className='min-w-0'>
                <p className='text-[10px] uppercase tracking-widest font-bold text-muted-foreground/60 truncate'>
                  {label}
                </p>
                <p
                  className={`text-2xl font-black tabular-nums tracking-tight ${textColor}`}
                >
                  {value}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* ─── Tab Content ─── */}
        {activeTab === 1 && (
          <div className='flex-1 flex flex-col min-h-0 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-hidden'>
            {/* Section Navigator */}
            <div className='shrink-0 flex items-center justify-between bg-card/50 p-4 rounded-2xl border border-border/50'>
              <div className='flex items-center gap-3'>
                <div className='h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center'>
                  <Calendar className='h-5 w-5 text-primary' />
                </div>
                <div>
                  <h2 className='text-sm font-bold text-foreground'>
                    Weekly Performance
                  </h2>
                  <p className='text-[10px] text-muted-foreground uppercase tracking-widest font-bold'>
                    {summaryTotal} active employees
                  </p>
                </div>
              </div>

              {/* Week Navigator */}
              <div className='flex items-center gap-1.5'>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-8 w-8 p-0 rounded-lg border-border hover:bg-muted transition-colors'
                  onClick={() => {
                    setWeekOffset(prev => prev - 1)
                    setSummaryPage(1)
                  }}
                >
                  <ChevronLeft className='h-4 w-4' />
                </Button>
                <div className='px-4 py-1.5 bg-muted/30 border border-border rounded-lg shadow-sm min-w-[200px] text-center'>
                  <span className='text-xs font-bold text-muted-foreground'>
                    {weekLabel}
                  </span>
                </div>
                <Button
                  variant='outline'
                  size='sm'
                  className='h-8 w-8 p-0 rounded-lg border-border hover:bg-muted transition-colors'
                  onClick={() => {
                    setWeekOffset(prev => prev + 1)
                    setSummaryPage(1)
                  }}
                >
                  <ChevronRight className='h-4 w-4' />
                </Button>
                {weekOffset !== 0 && (
                  <Button
                    variant='outline'
                    size='sm'
                    className='h-8 px-3 text-xs font-bold rounded-lg ml-1 border-border hover:bg-muted transition-colors'
                    onClick={() => {
                      setWeekOffset(0)
                      setSummaryPage(1)
                    }}
                  >
                    Current Week
                  </Button>
                )}
              </div>
            </div>

            {/* Weekly Summary Table */}
            <div className='flex-1 bg-card rounded-sm border border-border shadow-sm overflow-auto min-h-0'>
              <Table className='border-none'>
                <TableHeader className='bg-muted/50 border-b border-border'>
                  <TableRow className='hover:bg-transparent'>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] uppercase tracking-wider cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleSummarySort('user_name')}
                    >
                      Employee {getSummarySortIcon('user_name')}
                    </TableHead>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] uppercase tracking-wider cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleSummarySort('team')}
                    >
                      Team {getSummarySortIcon('team')}
                    </TableHead>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] uppercase tracking-wider text-center cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleSummarySort('total_hours')}
                    >
                      Hours {getSummarySortIcon('total_hours')}
                    </TableHead>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] uppercase tracking-wider text-center cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleSummarySort('flagged_entries')}
                    >
                      Flagged {getSummarySortIcon('flagged_entries')}
                    </TableHead>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] uppercase tracking-wider text-center cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleSummarySort('final_score')}
                    >
                      Final Score {getSummarySortIcon('final_score')}
                    </TableHead>
                    <TableHead className='py-4 px-6 font-medium text-muted-foreground text-[11px] uppercase tracking-wider text-center'>
                      Status
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summaryLoading || summaryFetching ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 8 }).map((_, j) => (
                          <TableCell key={j} className='py-5 px-6'>
                            <div className='h-5 bg-muted rounded-lg animate-pulse' />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : summaryError ? (
                    <TableRow>
                      <TableCell colSpan={8} className='py-24 text-center'>
                        <div className='flex flex-col items-center justify-center space-y-4 text-destructive'>
                          <AlertCircle className='h-10 w-10 opacity-20' />
                          <p className='text-muted-foreground text-sm font-medium'>
                            Failed to load weekly summary
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : summaryResults.length > 0 ? (
                    summaryResults.map((item: WeeklySummaryItem) => (
                      <TableRow
                        key={item.user_id}
                        className={cn(
                          'group hover:bg-muted/30 transition-colors border-b border-border last:border-0 ',
                          item.hours_hard_fail && 'bg-destructive/5'
                        )}
                      >
                        {/* Employee */}
                        <TableCell className='py-5 px-6'>
                          <div className='flex items-center gap-3'>
                            <div
                              className={cn(
                                'h-9 w-9 rounded-xl flex items-center justify-center text-primary-foreground font-bold text-xs shadow-sm shrink-0',
                                item.pass ? 'bg-primary' : 'bg-red-500'
                              )}
                            >
                              {item.user_name?.charAt(0) || 'U'}
                            </div>
                            <span className='font-bold text-foreground text-sm'>
                              {item.user_name}
                            </span>
                          </div>
                        </TableCell>

                        {/* Team */}
                        <TableCell className='py-5 px-6'>
                          {item.team ? (
                            <span className='inline-flex px-2.5 py-1 rounded-lg bg-muted border border-border text-[11px] font-bold text-muted-foreground'>
                              {item.team}
                            </span>
                          ) : (
                            <span className='text-xs text-muted-foreground/30'>
                              —
                            </span>
                          )}
                        </TableCell>

                        {/* Total Hours */}
                        <TableCell className='py-5 px-6 text-center'>
                          <div className='flex flex-col items-center gap-0.5'>
                            <span
                              className={cn(
                                'text-sm font-bold font-mono',
                                item.hours_hard_fail
                                  ? 'text-red-500'
                                  : item.total_hours >=
                                      (item.hours_target ?? 45)
                                    ? 'text-emerald-500'
                                    : 'text-amber-500'
                              )}
                            >
                              {formatHours(item.total_hours)}h
                            </span>
                            {item.hours_target != null && (
                              <span className='text-[9px] text-muted-foreground font-medium font-mono'>
                                / {formatHours(item.hours_target)}h target
                              </span>
                            )}
                            {item.hours_hard_fail && (
                              <span className='text-[9px] font-bold text-red-500 uppercase tracking-widest'>
                                Low Hours
                              </span>
                            )}
                          </div>
                        </TableCell>

                        {/* Flagged */}
                        <TableCell className='py-5 px-6 text-center'>
                          {item.flagged_entries > 0 ? (
                            <span className='inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs font-bold text-amber-600'>
                              <Flag className='h-3 w-3' />
                              {item.flagged_entries}
                            </span>
                          ) : (
                            <span className='inline-flex items-center px-2 py-1 rounded-lg bg-muted border border-border text-xs font-bold text-muted-foreground/40'>
                              0
                            </span>
                          )}
                        </TableCell>

                        {/* Final Score */}
                        <TableCell className='py-5 px-6 text-center'>
                          <div
                            className={cn(
                              'inline-flex items-center justify-center px-3 py-1 rounded-lg border text-sm font-black min-w-[40px]',
                              item.final_score >= 9
                                ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                                : item.final_score >= 7
                                  ? 'bg-amber-500/10 text-amber-600 border-amber-500/20'
                                  : 'bg-red-500/10 text-red-600 border-red-500/20'
                            )}
                          >
                            {item.final_score}
                          </div>
                        </TableCell>

                        {/* Status */}
                        <TableCell className='py-5 px-6 text-center'>
                          <span
                            className={cn(
                              'inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-sm',
                              item.pass
                                ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 shadow-emerald-500/5'
                                : 'bg-red-500/10 text-red-600 border-red-500/20 shadow-red-500/5'
                            )}
                          >
                            <span
                              className={cn(
                                'h-1.5 w-1.5 rounded-full',
                                item.pass ? 'bg-emerald-500' : 'bg-red-500'
                              )}
                            />
                            {item.pass ? 'Pass' : 'Fail'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Button onClick={() => setSelectedUser(item)}>
                            <Eye className='h-4 w-4' />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} className='py-24 text-center'>
                        <div className='flex flex-col items-center justify-center space-y-4 text-muted-foreground'>
                          <div className='h-20 w-20 rounded-full bg-muted flex items-center justify-center'>
                            <TrendingUp className='h-10 w-10 opacity-20' />
                          </div>
                          <div>
                            <p className='text-sm font-black uppercase tracking-[0.2em]'>
                              No Summary Data
                            </p>
                            <p className='text-xs mt-1'>
                              No weekly summary available for this period.
                            </p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Weekly Summary Pagination */}
            {summaryTotal > 0 && (
              <div className='shrink-0'>
                <CustomPagination
                  limit={summaryLimit}
                  setLimit={setSummaryLimit}
                  page={summaryPage}
                  setPage={setSummaryPage}
                  totalPages={summaryTotalPages}
                  totalRecords={summaryTotal}
                />
              </div>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════════════
            SECTION 2 — Description Analysis (full width)
            ════════════════════════════════════════════════════ */}
        {activeTab === 2 && (
          <div className='flex-1 flex flex-col min-h-0 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 overflow-hidden'>
            <div className='flex-1 bg-card rounded-sm border border-border shadow-sm overflow-auto min-h-0'>
              <Table className='border-none'>
                <TableHeader className='bg-muted/50 border-b border-border'>
                  <TableRow className='hover:bg-transparent'>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] truncate uppercase tracking-wider cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleDescSort('user_name')}
                    >
                      Employee {getDescSortIcon('user_name')}
                    </TableHead>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] truncate uppercase tracking-wider cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleDescSort('start_time')}
                    >
                      Date {getDescSortIcon('start_time')}
                    </TableHead>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] truncateuppercase tracking-wider cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleDescSort('description')}
                    >
                      Description {getDescSortIcon('description')}
                    </TableHead>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground text-[11px] truncate uppercase tracking-wider text-center cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleDescSort('duration_seconds')}
                    >
                      Duration {getDescSortIcon('duration_seconds')}
                    </TableHead>
                    <TableHead
                      className='py-4 px-6 font-medium text-muted-foreground truncate text-[11px] uppercase tracking-wider text-center cursor-pointer hover:bg-muted/30 transition-colors'
                      onClick={() => handleDescSort('rating')}
                    >
                      AI Rating {getDescSortIcon('rating')}
                    </TableHead>
                    <TableHead className='py-4 px-6 font-medium text-muted-foreground text-[11px] uppercase tracking-wider text-right'>
                      Insights
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading || isFetching ? (
                    Array.from({ length: 5 }).map((_, i) => (
                      <TableRow key={i}>
                        {Array.from({ length: 6 }).map((_, j) => (
                          <TableCell key={j} className='py-6 px-6'>
                            <div className='h-5 bg-muted rounded-lg animate-pulse' />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : error ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className='py-24 text-center truncate'
                      >
                        <div className='flex flex-col items-center justify-center space-y-4 text-destructive'>
                          <AlertCircle className='h-10 w-10 opacity-20' />
                          <p className='text-muted-foreground text-sm font-medium'>
                            No description analysis found
                          </p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : results.length > 0 ? (
                    results.map((item: DescriptionAnalysisItem) => (
                      <TableRow
                        key={item.analysis_id}
                        className='group hover:bg-muted/30 transition-colors border-b border-border last:border-0'
                      >
                        {/* Employee Cell */}
                        <TableCell className='py-5 px-6 truncate'>
                          <div className='flex items-center gap-3'>
                            <div className='w-8 h-8 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center border border-border shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200'>
                              {item?.profile_picture ? (
                                <img
                                  src={item.profile_picture}
                                  alt={item.user_name ?? 'User Profile'}
                                  className='w-full h-full object-cover'
                                  onError={e => {
                                    ;(
                                      e.target as HTMLImageElement
                                    ).style.display = 'none'
                                  }}
                                />
                              ) : (
                                <span className='text-xs font-bold text-primary'>
                                  {item?.user_name
                                    ? item.user_name.charAt(0).toUpperCase()
                                    : ''}
                                </span>
                              )}
                            </div>
                            <span className='font-bold text-foreground text-sm truncate max-w-[120px]'>
                              {item.user_name}
                            </span>
                          </div>
                        </TableCell>

                        {/* Date Cell */}
                        <TableCell className='py-5 px-6 whitespace-nowrap truncate'>
                          <span className='text-xs font-bold text-foreground'>
                            {format(new Date(item.start_time), 'MMM dd, yyyy')}
                          </span>
                        </TableCell>

                        {/* Content Cell */}
                        <TableCell className='py-5 px-6 max-w-[320px] truncate'>
                          <div className='space-y-2'>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <p className='text-[13px] text-muted-foreground font-medium leading-normal italic truncate cursor-help group-hover:text-foreground transition-colors'>
                                  {item.description ??
                                    'No description provided'}
                                </p>
                              </TooltipTrigger>
                              <TooltipContent className='max-w-xs bg-popover text-popover-foreground p-3 rounded-lg border border-border shadow-xl'>
                                <p className='text-xs leading-relaxed italic'>
                                  {item.description ??
                                    'No description provided'}
                                </p>
                              </TooltipContent>
                            </Tooltip>
                            <div className='flex items-center gap-1.5 flex-wrap'>
                              {item.tags?.split(',').map((tag: string) => (
                                <span
                                  key={tag}
                                  className='px-2 py-0.5 rounded-md bg-muted border border-border text-[9px] font-bold text-muted-foreground uppercase tracking-tight'
                                >
                                  {tag.trim() || 'No tag provided'}
                                </span>
                              ))}
                            </div>
                          </div>
                        </TableCell>

                        {/* Duration Cell */}
                        <TableCell className='py-5 px-6 text-center truncate'>
                          <span className='inline-flex items-center justify-center px-2 py-1 rounded-lg bg-muted text-[13px] font-bold text-foreground font-mono'>
                            {Math.floor(item.duration_seconds / 3600)}h{' '}
                            {Math.floor((item.duration_seconds % 3600) / 60)}m
                          </span>
                        </TableCell>

                        {/* Score Cell */}
                        <TableCell className='py-5 px-6'>
                          <div className='flex flex-col items-center gap-1.5'>
                            <div
                              className={cn(
                                'h-8 w-12 rounded-lg flex items-center justify-center text-xs font-black border shadow-sm transition-transform group-hover:scale-110',
                                item.rating >= 6
                                  ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'
                                  : 'bg-red-500/10 text-red-600 border-red-500/20'
                              )}
                            >
                              {item.rating}/10
                            </div>
                            <span
                              className={cn(
                                'text-[9px] font-black uppercase tracking-widest',
                                item.rating >= 6
                                  ? 'text-emerald-500/70'
                                  : 'text-red-500/70'
                              )}
                            >
                              {item.rating >= 6 ? 'Quality' : 'Warning'}
                            </span>
                          </div>
                        </TableCell>

                        {/* Insights Cell */}
                        <TableCell className='py-5 px-6 text-right truncate'>
                          <div className='flex items-center justify-end gap-2'>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  className='h-8 px-4 rounded-xl border-destructive/20 text-foreground bg-card hover:bg-destructive/10 text-[10px] font-black uppercase tracking-widest transition-all duration-300'
                                >
                                  Review Issue
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className='w-[320px] p-0 shadow-2xl border-border rounded-2xl bg-popover/95 backdrop-blur-xl overflow-hidden'
                                side='left'
                                sideOffset={15}
                              >
                                <div className='bg-destructive/10 p-4 border-b border-destructive/10 flex items-center gap-2'>
                                  <AlertCircle className='h-4 w-4 text-destructive' />
                                  <h4 className='font-black text-destructive text-[10px] uppercase tracking-[0.2em]'>
                                    Audit Report
                                  </h4>
                                </div>
                                <div className='p-5'>
                                  <p className='text-[13px] text-foreground font-medium leading-relaxed'>
                                    {item.issue}
                                  </p>
                                </div>
                              </PopoverContent>
                            </Popover>

                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  variant='default'
                                  size='sm'
                                  className='h-8 px-4 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20 hover:shadow-primary/30 transition-all duration-300 text-[10px] font-black uppercase tracking-widest'
                                >
                                  Suggestions
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent
                                className='w-[360px] p-0 shadow-2xl border-border rounded-2xl bg-popover/95 backdrop-blur-xl overflow-hidden'
                                side='left'
                                sideOffset={15}
                              >
                                <div className='bg-primary/10 p-4 border-b border-primary/10 flex items-center gap-2'>
                                  <Sparkles className='h-4 w-4 text-primary' />
                                  <h4 className='font-black text-primary text-[10px] uppercase tracking-[0.2em]'>
                                    AI Optimization
                                  </h4>
                                </div>
                                <div className='p-5'>
                                  <div className='p-4 rounded-xl bg-muted/50 border border-border relative group/suggestion'>
                                    <Quote className='absolute -top-2 -left-2 h-6 w-6 text-primary/20 group-hover:text-primary transition-colors' />
                                    <p className='text-[13px] text-foreground font-semibold leading-relaxed italic relative z-10'>
                                      {item.suggestion}
                                    </p>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        className='py-24 text-center truncate'
                      >
                        <div className='flex flex-col items-center justify-center space-y-4 text-muted-foreground'>
                          <FileText className='h-10 w-10 opacity-20' />
                          <div>
                            <p className='text-sm font-black uppercase tracking-[0.2em]'>
                              No Intelligence Found
                            </p>
                            <p className='text-xs mt-1'>
                              Try adjusting your filters or search terms.
                            </p>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Pagination Section */}
            {totalItems > 0 && (
              <div className='shrink-0'>
                <CustomPagination
                  limit={limit}
                  setLimit={setLimit}
                  page={page}
                  setPage={setPage}
                  totalPages={totalPages}
                  totalRecords={totalItems}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── User Detail Dialog ─── */}
      <Dialog
        open={!!selectedUser}
        onOpenChange={open => !open && setSelectedUser(null)}
      >
        <DialogContent className='w-[95vw] max-w-[95vw] h-[92vh] max-h-[92vh] flex flex-col p-0 gap-0 overflow-hidden rounded-2xl'>
          {selectedUser && (
            <>
              {/* ── Fixed header ── */}
              <div
                className={cn(
                  'shrink-0 px-8 py-6 border-b border-border',
                  selectedUser.pass ? 'bg-card' : 'bg-destructive/5'
                )}
              >
                <div className='flex items-center gap-5'>
                  <div
                    className={cn(
                      'h-14 w-14 rounded-2xl flex items-center justify-center text-primary-foreground font-black text-xl shadow-lg shrink-0',
                      selectedUser.pass
                        ? 'bg-primary shadow-primary/20'
                        : 'bg-destructive shadow-destructive/20'
                    )}
                  >
                    {selectedUser.user_name?.charAt(0) || 'U'}
                  </div>
                  <div>
                    <h2 className='text-2xl font-black text-foreground tracking-tight leading-tight'>
                      {selectedUser.user_name}
                    </h2>
                    <p className='text-sm text-muted-foreground font-medium flex items-center gap-2'>
                      <Users className='h-3.5 w-3.5' />
                      {selectedUser.team ?? 'No team'} · Performance Analysis
                    </p>
                  </div>
                  <div className='ml-auto flex items-center gap-3'>
                    {selectedUser.source && (
                      <span
                        className={cn(
                          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all duration-300',
                          selectedUser.source === 'ai_audit'
                            ? 'bg-indigo-500/10 text-indigo-500 border-indigo-500/20 shadow-sm shadow-indigo-500/5'
                            : 'bg-muted text-muted-foreground border-border'
                        )}
                      >
                        <Zap className='h-3.5 w-3.5 fill-current' />
                        {selectedUser.source === 'ai_audit'
                          ? 'AI Verified'
                          : 'Manual Audit'}
                      </span>
                    )}
                    <span
                      className={cn(
                        'inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest border shadow-sm transition-all duration-300',
                        selectedUser.pass
                          ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20 shadow-emerald-500/5'
                          : 'bg-destructive/10 text-destructive border-destructive/20 shadow-destructive/5'
                      )}
                    >
                      <span
                        className={cn(
                          'h-1.5 w-1.5 rounded-full',
                          selectedUser.pass
                            ? 'bg-emerald-500'
                            : 'bg-destructive'
                        )}
                      />
                      {selectedUser.pass ? 'Standard Passed' : 'Audit Failed'}
                    </span>
                  </div>
                </div>
              </div>

              {/* ── Scrollable body ── */}
              <div className='flex-1 overflow-y-auto'>
                <div className='p-8 space-y-8'>
                  {/* ── Row 1: Stats + Score breakdown side by side ── */}
                  <div className='grid grid-cols-3 gap-6'>
                    {/* Stat cards */}
                    <div className='col-span-1 grid grid-cols-2 gap-4 content-start'>
                      <div className='flex flex-col items-center p-5 rounded-2xl bg-muted/30 border border-border group hover:border-primary/20 transition-all'>
                        <span className='text-[9px] uppercase tracking-[0.2em] font-black text-muted-foreground/60 mb-2'>
                          Hours Logged
                        </span>
                        <span
                          className={cn(
                            'text-2xl font-black tabular-nums tracking-tight',
                            selectedUser.hours_hard_fail
                              ? 'text-destructive'
                              : selectedUser.total_hours >=
                                  (selectedUser.hours_target ?? 45)
                                ? 'text-emerald-500'
                                : 'text-amber-500'
                          )}
                        >
                          {formatHours(selectedUser.total_hours)}h
                        </span>
                        {selectedUser.hours_target != null && (
                          <span className='text-[10px] text-muted-foreground/60 font-bold mt-1'>
                            / {formatHours(selectedUser.hours_target)}h target
                          </span>
                        )}
                        {selectedUser.hours_hard_fail && (
                          <span className='mt-1 text-[9px] font-bold text-destructive uppercase tracking-widest bg-destructive/10 px-2.5 py-0.5 rounded-full'>
                            Low Hours
                          </span>
                        )}
                      </div>
                      <div className='flex flex-col items-center p-5 rounded-2xl bg-muted/30 border border-border group hover:border-primary/20 transition-all'>
                        <span className='text-[9px] uppercase tracking-[0.2em] font-black text-muted-foreground/60 mb-2'>
                          Entries
                        </span>
                        <span className='text-2xl font-black tabular-nums tracking-tight text-foreground'>
                          {selectedUser.total_entries ??
                            selectedUser.description_analysis?.length ??
                            '0'}
                        </span>
                      </div>
                      <div className='flex flex-col items-center p-5 rounded-2xl bg-amber-500/5 border border-amber-500/10 group hover:border-amber-500/30 transition-all'>
                        <span className='text-[9px] uppercase tracking-[0.2em] font-black text-amber-500/60 mb-2'>
                          Flagged
                        </span>
                        <span className='text-2xl font-black tabular-nums tracking-tight text-amber-500'>
                          {selectedUser.flagged_entries}
                        </span>
                      </div>
                      <div
                        className={cn(
                          'flex flex-col items-center p-5 rounded-2xl border transition-all duration-300',
                          selectedUser.final_score >= 9
                            ? 'bg-emerald-500/5 border-emerald-500/10 hover:border-emerald-500/30'
                            : selectedUser.final_score >= 7
                              ? 'bg-amber-500/5 border-amber-500/10 hover:border-amber-500/30'
                              : 'bg-destructive/5 border-destructive/10 hover:border-destructive/30'
                        )}
                      >
                        <span
                          className={cn(
                            'text-[9px] uppercase tracking-[0.2em] font-black mb-2',
                            selectedUser.final_score >= 9
                              ? 'text-emerald-500'
                              : selectedUser.final_score >= 7
                                ? 'text-amber-500'
                                : 'text-destructive'
                          )}
                        >
                          Final Score
                        </span>
                        <div className='flex items-baseline gap-0.5'>
                          <span
                            className={cn(
                              'text-3xl font-black tracking-tighter',
                              selectedUser.final_score >= 9
                                ? 'text-emerald-600'
                                : selectedUser.final_score >= 7
                                  ? 'text-amber-600'
                                  : 'text-destructive'
                            )}
                          >
                            {selectedUser.final_score}
                          </span>
                          <span className='text-xs font-black text-muted-foreground/40'>
                            /10
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Score breakdown */}
                    <div className='col-span-2 p-7 rounded-3xl bg-card border border-border space-y-6 shadow-sm'>
                      <div>
                        <p className='text-[10px] uppercase tracking-[0.2em] font-black text-muted-foreground/60 mb-1'>
                          Performance Metrics
                        </p>
                        <h3 className='text-lg font-black text-foreground'>
                          Intelligence Scorecard
                        </h3>
                      </div>
                      <div className='space-y-6'>
                        {[
                          {
                            label: 'Log Consistency (Hours)',
                            value: selectedUser.hours_score,
                            color: 'bg-indigo-500',
                          },
                          {
                            label: 'Semantic Quality (Descriptions)',
                            value: selectedUser.description_score,
                            color: 'bg-primary',
                          },
                          {
                            label: 'Tag Relevance',
                            value: selectedUser.tag_score,
                            color: 'bg-emerald-500',
                          },
                          {
                            label: 'Log Periodicity (Billability)',
                            value: selectedUser.billability_score,
                            color: 'bg-amber-500',
                          },
                        ].map(({ label, value, color }) => (
                          <div key={label} className='space-y-2.5'>
                            <div className='flex justify-between items-end'>
                              <span className='text-[11px] font-black text-foreground uppercase tracking-widest'>
                                {label}
                              </span>
                              <div className='flex items-baseline gap-1'>
                                <span className='text-sm font-black text-foreground'>
                                  {value}
                                </span>
                                <span className='text-[10px] font-bold text-muted-foreground/40'>
                                  /10
                                </span>
                              </div>
                            </div>
                            <div className='h-2 w-full bg-muted rounded-full overflow-hidden'>
                              <div
                                className={cn(
                                  'h-full rounded-full transition-all duration-1000',
                                  color
                                )}
                                style={{
                                  width: `${Math.min(value * 10, 100)}%`,
                                }}
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* ── Row 2: Good points + Bad points ── */}
                  {(selectedUser.good_points?.length > 0 ||
                    selectedUser.bad_points?.length > 0) && (
                    <div className='grid grid-cols-2 gap-6'>
                      {selectedUser.good_points?.length > 0 && (
                        <div className='p-6 rounded-3xl bg-emerald-500/5 border border-emerald-500/10 shadow-sm'>
                          <p className='text-[10px] uppercase tracking-[0.2em] font-black text-emerald-600 mb-4 flex items-center gap-2'>
                            <CheckCircle2 className='h-4 w-4' /> Performance
                            Highlights
                          </p>
                          <ul className='space-y-3'>
                            {selectedUser.good_points.map((pt, i) => (
                              <li
                                key={i}
                                className='flex items-start gap-3 group/item'
                              >
                                <div className='mt-1 h-4 w-4 rounded-full bg-emerald-500/20 border border-emerald-500/30 flex items-center justify-center shrink-0 text-[10px] font-black text-emerald-600 transition-transform group-hover/item:scale-110'>
                                  ✓
                                </div>
                                <span className='text-sm text-foreground font-medium leading-relaxed italic'>
                                  {pt}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {selectedUser.bad_points?.length > 0 && (
                        <div className='p-6 rounded-3xl bg-destructive/5 border border-destructive/10 shadow-sm'>
                          <p className='text-[10px] uppercase tracking-[0.2em] font-black text-destructive mb-4 flex items-center gap-2'>
                            <AlertTriangle className='h-4 w-4' /> Areas to
                            Improve
                          </p>
                          <ul className='space-y-3'>
                            {selectedUser.bad_points.map((pt, i) => (
                              <li
                                key={i}
                                className='flex items-start gap-3 group/item'
                              >
                                <div className='mt-1 h-4 w-4 rounded-full bg-destructive/20 border border-destructive/30 flex items-center justify-center shrink-0 text-[10px] font-black text-destructive transition-transform group-hover/item:scale-110'>
                                  ✗
                                </div>
                                <span className='text-sm text-foreground font-medium leading-relaxed italic'>
                                  {pt}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* ── Critical flags ── */}
                  {selectedUser.critical_flags?.length > 0 && (
                    <div className='p-6 rounded-3xl bg-amber-500/5 border border-amber-500/10 space-y-4 shadow-sm'>
                      <p className='text-[10px] uppercase tracking-[0.2em] font-black text-amber-600 flex items-center gap-2'>
                        <Flag className='h-4 w-4 fill-current' /> Critical
                        Intelligence Flags
                      </p>
                      <div className='flex flex-wrap gap-2.5'>
                        {selectedUser.critical_flags.map((flag, i) => (
                          <span
                            key={i}
                            className='inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-card border border-amber-500/20 text-xs font-bold text-amber-700 shadow-sm hover:scale-[1.02] transition-transform cursor-default'
                          >
                            <div className='h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse' />
                            {flag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Timesheet Entry Analysis table ── */}
                  {selectedUser.description_analysis?.length > 0 && (
                    <div className='space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-300'>
                      <div className='flex items-center justify-between bg-card/50 p-4 rounded-2xl border border-border/50'>
                        <div className='flex items-center gap-3'>
                          <div className='p-2 bg-primary/10 rounded-xl'>
                            <FileSearch className='h-4 w-4 text-primary' />
                          </div>
                          <div>
                            <h3 className='text-sm font-black text-foreground'>
                              Intelligence Audit Breakdown
                            </h3>
                            <p className='text-[10px] text-muted-foreground uppercase tracking-widest font-bold'>
                              Per-entry detailed analysis
                            </p>
                          </div>
                        </div>
                        <span className='px-3 py-1 rounded-full bg-muted text-[10px] font-black text-muted-foreground uppercase tracking-widest border border-border shadow-sm'>
                          {selectedUser.description_analysis.length} Entries
                        </span>
                      </div>
                      <div className='rounded-3xl border border-border bg-card shadow-sm overflow-hidden'>
                        <Table>
                          <TableHeader className='bg-muted/50 border-b border-border'>
                            <TableRow className='hover:bg-transparent'>
                              <TableHead className='py-4 px-6 text-[10px] font-medium text-muted-foreground uppercase tracking-widest'>
                                Timeline
                              </TableHead>
                              <TableHead className='py-4 px-6 text-[10px] font-medium text-muted-foreground uppercase tracking-widest'>
                                Task & Context
                              </TableHead>
                              <TableHead className='py-4 px-6 text-[10px] font-medium text-muted-foreground uppercase tracking-widest text-center'>
                                Analytics
                              </TableHead>
                              <TableHead className='py-4 px-6 text-[10px] font-medium text-muted-foreground uppercase tracking-widest'>
                                AI Observations
                              </TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {selectedUser.description_analysis.map(
                              (entry: WeeklyDescriptionEntry) => (
                                <TableRow
                                  key={entry.entry_id}
                                  className={cn(
                                    'group hover:bg-muted/30 transition-colors border-b border-border last:border-0',
                                    entry.rating <= 3
                                      ? 'bg-red-500/[0.02]'
                                      : entry.rating <= 6
                                        ? 'bg-amber-500/[0.02]'
                                        : 'bg-transparent'
                                  )}
                                >
                                  <TableCell className='py-5 px-6 whitespace-nowrap'>
                                    <div className='flex flex-col gap-1'>
                                      <span className='text-sm font-black text-foreground tabular-nums'>
                                        {format(new Date(entry.date), 'MMM dd')}
                                      </span>
                                      <span className='text-[10px] font-bold text-muted-foreground uppercase tracking-widest'>
                                        {format(new Date(entry.date), 'EEEE')}
                                      </span>
                                    </div>
                                  </TableCell>

                                  <TableCell className='py-5 px-6 min-w-[300px]'>
                                    <div className='space-y-2'>
                                      <div className='flex items-center gap-2'>
                                        <span className='px-2 py-0.5 rounded bg-primary/10 text-[10px] font-black text-primary uppercase tracking-tight'>
                                          {entry.task_name}
                                        </span>
                                      </div>
                                      <div className='relative group/desc'>
                                        <p className='text-[13px] text-foreground font-medium leading-relaxed line-clamp-2 italic'>
                                          "{entry.description}"
                                        </p>
                                        <div className='flex items-center gap-1.5 mt-2 flex-wrap'>
                                          {entry.tags
                                            ?.split(',')
                                            .map((tag: string) => (
                                              <span
                                                key={tag}
                                                className='px-2 py-0.5 rounded-md bg-muted border border-border text-[9px] font-bold text-muted-foreground uppercase tracking-tight'
                                              >
                                                {tag.trim()}
                                              </span>
                                            ))}
                                        </div>
                                      </div>
                                    </div>
                                  </TableCell>

                                  <TableCell className='py-5 px-6'>
                                    <div className='flex flex-col items-center gap-3'>
                                      <div className='flex flex-col items-center'>
                                        <span className='text-sm font-black text-foreground font-mono'>
                                          {Math.floor(
                                            entry.duration_seconds / 3600
                                          )}
                                          h{' '}
                                          {Math.floor(
                                            (entry.duration_seconds % 3600) / 60
                                          )}
                                          m
                                        </span>
                                        <span
                                          className={cn(
                                            'text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded mt-1',
                                            entry.billable
                                              ? 'bg-emerald-500/10 text-emerald-600'
                                              : 'bg-muted text-muted-foreground'
                                          )}
                                        >
                                          {entry.billable
                                            ? 'Billable'
                                            : 'Non-Billable'}
                                        </span>
                                      </div>
                                      <div className='scale-75'>
                                        <RatingStars rating={entry.rating} />
                                      </div>
                                    </div>
                                  </TableCell>

                                  <TableCell className='py-5 px-6 min-w-[250px]'>
                                    <div className='space-y-3'>
                                      {entry.issue && (
                                        <div className='flex items-start gap-2 p-2.5 rounded-xl bg-red-500/5 border border-red-500/10'>
                                          <AlertCircle className='h-3.5 w-3.5 text-red-500 shrink-0 mt-0.5' />
                                          <p className='text-[11px] text-red-600 font-bold leading-relaxed'>
                                            {entry.issue}
                                          </p>
                                        </div>
                                      )}
                                      {entry.suggestion && (
                                        <div className='flex items-start gap-2 p-2.5 rounded-xl bg-primary/5 border border-primary/10'>
                                          <Sparkles className='h-3.5 w-3.5 text-primary shrink-0 mt-0.5' />
                                          <p className='text-[11px] text-foreground font-medium leading-relaxed italic'>
                                            {entry.suggestion}
                                          </p>
                                        </div>
                                      )}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}

                  {/* ── ClickUp message ── */}
                  {selectedUser.clickup_message && (
                    <div className='space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-400'>
                      <div className='flex items-center gap-3 p-4 bg-muted/30 border border-border/50 rounded-2xl'>
                        <div className='p-2 bg-indigo-500/10 rounded-xl'>
                          <MessageSquare className='h-4 w-4 text-indigo-500' />
                        </div>
                        <div>
                          <h3 className='text-sm font-black text-foreground'>
                            AI Auto-Generated Report
                          </h3>
                          <p className='text-[10px] text-muted-foreground uppercase tracking-widest font-bold'>
                            Ready for communication
                          </p>
                        </div>
                      </div>
                      <div className='p-6 rounded-3xl bg-card border border-border shadow-inner relative group'>
                        <Quote className='absolute top-4 right-4 h-8 w-8 text-muted/20 transition-colors group-hover:text-primary/10' />
                        <pre className='text-[13px] text-foreground whitespace-pre-wrap font-medium leading-relaxed relative z-10'>
                          {selectedUser.clickup_message}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  )
}

export default DescriptionAnalysis
